import React, { useContext, useEffect } from "react";
import FilterPopup from "../FilterPopup";
import { Button } from "semantic-ui-react";
import NumberInput from "components/NumberInput";
import { focusOnMount } from "utilities/reactUtils";
import { FilterContext } from "../FilterContext";

interface Props {
  maxCapacity: number
}

const formatLabel = (min: number, max: number, maxCapacity: number) => {
  if (min === 0) {
    return RESX.Capacity.LessThanXPax.replace("{0}", max.toString());
  } else if (max === maxCapacity) {
    return RESX.Capacity.MoreThanXPax.replace("{0}", min.toString());
  } else {
    return RESX.Capacity.BetweenXandYPax.replace("{0}", min.toString()).replace("{1}", max.toString());
  }
};

const VenueCapacityFilter = (props: Props) => {
  const title = RESX.Venue.Capacity;

  const { register, getValue, setInitialValue, setValue } = useContext(FilterContext);
  register("venueCapacity", {
    getTitle: () => title,
    renderSelection: (filter) => <>{formatLabel(filter.min, filter.max, props.maxCapacity)}</>,
    hasValue: (filter) => filter.min !== 0 || filter.max !== props.maxCapacity,
    reset: () => ({
      min: 0,
      max: props.maxCapacity
    }),
    areEqual: (a, b) => {
      return a.min === b.min && a.max === b.max;
    }
  });
  useEffect(() => {
    setInitialValue("venueCapacity", { min: 0, max: props.maxCapacity });
  }, []);

  const [popupOpen, setPopupOpen] = React.useState<boolean>(false);
  const [min, setMin] = React.useState(0);
  const [max, setMax] = React.useState(props.maxCapacity);
  const hasError = min > max;
  const isEmpty = min === 0 && max === props.maxCapacity;

  const onPopupClose = () => {
    onApply();
  };

  const onPopupOpen = () => {
    const value = getValue("venueCapacity");
    setMin(value.min);
    setMax(value.max);
    setPopupOpen(true);
  };

  const onApply = (e?) => {
    e?.preventDefault();
    if (!hasError) {
      setValue("venueCapacity", {
        min,
        max
      });
    }
    setPopupOpen(false);
  };

  return <div>
    <FilterPopup
      filterButtonLabel={RESX.Venue.Capacity}
      content={<form onSubmit={onApply}>
        <div className="venue-capacity-filter-inputs">
          <NumberInput
            innerRef={focusOnMount}
            value={min === 0 ? "" : min}
            onChanged={(newValue) => { newValue.value !== undefined ? setMin(newValue.value) : setMin(0); }}
            decimalPoints={0}
            canBeEmpty={true}
            canBeNegative={false}
            maxLength={6}
            placeholder={RESX.Capacity.Minimum}
          />
          <NumberInput
            value={max === props.maxCapacity ? "" : max}
            onChanged={(newValue) => { newValue.value !== undefined ? setMax(newValue.value) : setMax(props.maxCapacity); }}
            decimalPoints={0}
            canBeEmpty={true}
            canBeNegative={false}
            maxLength={6}
            placeholder={RESX.Capacity.Maximum}
          />
        </div>
        {hasError && <div className="venue-capacity-filter-error body-text-semibold">{RESX.Capacity.MinCannotExceedMax}</div>}
        {!hasError && <div className="venue-capacity-filter-text body-text-regular" style={{ visibility: isEmpty ? "hidden" : "visible" }}>
          {formatLabel(min, max, props.maxCapacity)}
        </div>}
        <Button fluid type="submit" primary>
          {RESX.SelectionManager.Apply}
        </Button>
      </form>}
      width={300}
      open={popupOpen}
      onClose={onPopupClose}
      onOpen={onPopupOpen}
    />
  </div>;
};

export default VenueCapacityFilter;
